import type {
  LibraryManifest,
  LibraryVersionedResource,
  LibrariesInstallationType,
  LIBRARY_ACTION,
  LibraryInstallationSummary,
} from '@packages/systems/libraries';
import {prebuiltLayoutsLibraryManifest} from '@packages/systems/core/plugins';

export enum LIBRARIES_FETCH_STATE {
  requested = 'requested',
  succeeded = 'succeeded',
  failed = 'failed',
  initialized = 'initialized',
}

export type LibraryPublishingState = {
  status: 'requested' | 'succeeded' | 'failed' | 'initialized';
  errorMessage?: string;
};

export type LocalLibraryState = {
  manifest: {
    // Name of the library that will be visible to the user when they install library
    displayName: LibraryManifest<string>['displayName'];
    // Contains meta data (name, category, preview image and pointer to specific User component) about each User Component that will be exported as a Layout to Library
    exports: LibraryManifest<string>['exports'];
  };
  // Indicates whether the library is was being submitted and outcome of the submission
  publishingState: LibraryPublishingState;
  // Holds site and library data for all other sites that have this sites library installed.
  libraryInstallations: LibraryInstallationSummary[];
};

/**
 * State for tracking async manage actions (install, update, uninstall)
 * these run on the backend and we poll for updates
 */
export type ManageLibraryState = {
  action:
    | LIBRARY_ACTION.INSTALL
    | LIBRARY_ACTION.PULL_UPDATE
    | LIBRARY_ACTION.UNINSTALL;
  libraryResourceId: string;
  libraryProfileId: string;
  componentsToDetach?: string[];
  status: 'pending' | 'success' | 'failed';
  errorMessage?: string;
};

export type AvailableLibrariesState = {
  allLibraries: LibraryVersionedResource[];
  installations: LibrariesInstallationType;
  /**
   * Whether the site's installed library resources have loaded at least once.
   * `dynamicPluginsManager.status` is `IDLE` before and after.
   */
  installedResourcesLoaded: boolean;
  manageLibraryAction: ManageLibraryState | null;
  /**
   * Latest library resource published by this site
   */
  latestSiteLibrary: LibraryManifest<string> | null;
  /**
   * State for Current Site Library and any ongoing publish (i.e. sharing) and unpublish (i.e. unsharing) status
   */
  currentSiteLibrary: {
    libraryName: string;
    description: string;
    publishingState: LibraryPublishingState;
    isPushingUpdates: boolean;
  };
  fetchStatus: LIBRARIES_FETCH_STATE;
  libraryToInstallAfterDisablingCloning: LibraryManifest<string> | null;
};

export const defaultLoadedLibrariesState = {
  PrebuiltLayouts: prebuiltLayoutsLibraryManifest,
};

export const defaultLocalLibraryState: LocalLibraryState = {
  manifest: {
    displayName: '',
    exports: [],
  },
  publishingState: {status: 'initialized'},
  libraryInstallations: [],
};

export const defaultAvailableLibrariesState: AvailableLibrariesState = {
  allLibraries: [],
  installations: {},
  installedResourcesLoaded: false,
  manageLibraryAction: null,
  latestSiteLibrary: null,
  currentSiteLibrary: {
    libraryName: '',
    description: '',
    publishingState: {
      status: 'initialized',
    },
    isPushingUpdates: false,
  },
  fetchStatus: LIBRARIES_FETCH_STATE.initialized,
  libraryToInstallAfterDisablingCloning: null,
};
